/** Walmart defaults bundled as a portable naming profile. */
import { DEFAULT_FRAGMENT_JOIN_PCT } from "../domain/spineInference";
import { compileStudioProfile } from "./itemMasterWorkbook";
import {
  STUDIO_PROFILE_SCHEMA_VERSION,
  type StudioProfile,
  type StudioSectionSemantics,
} from "./studioProfile";

export const walmartStudioProfile = {
  schemaVersion: STUDIO_PROFILE_SCHEMA_VERSION,
  profileVersion: "1",
  merchantName: "Walmart",
  categoryName: "Laundry Detergent",
  itemIdentifier: "productCode",
  columns: { sku: "Item ID", name: "Product" },
  // `overlayAttr` bridges into the live SEMANTIC namespace (`brand`, `form`, `packType`) that inference runs on; `sectionSemantics` below maps each section's CDT columns onto those names.
  attributes: [
    {
      key: "section",
      label: "Subcategory",
      column: "CDT 0 · Section",
      pogField: "subCategory",
      overlayAttr: "variant",
    },
    // Brand and only brand: Downy, Gain, Tide, Snuggle — never Unstopables, which is a sub-brand and has its own rung below.
    {
      key: "brand",
      label: "Brand",
      column: "CDT 1",
      pogField: "brand",
      overlayAttr: "brand",
    },
    { key: "form", label: "Form", column: "CDT 2", pogField: "cdt2", overlayAttr: "form" },
    // Captions live only here — the studio reads them off the active profile, so a caption follows the attribute, never the column position.
    {
      key: "handle",
      label: "Pack type",
      column: "CDT 3",
      pogField: "cdt3",
      overlayAttr: "packType",
    },
    // Sub-brand, distinct from `variant`/flavour below, which is the SCENT.
    {
      key: "subBrand",
      label: "Variant / sub-brand",
      column: "Variant / sub-brand",
      overlayAttr: "subBrand",
    },
    { key: "size", label: "Size", column: "CDT 4", pogField: "size", overlayAttr: "size" },
    { key: "he", label: "HE / non-HE", column: "CDT 5", overlayAttr: "he" },
    {
      key: "variant",
      label: "Flavour / variant",
      column: "CDT 6",
      pogField: "variant",
      overlayAttr: "flavour",
    },
  ],
  /** What each section's CDT columns mean: the same column number carries a different attribute per section, so every mapping below is measured, not chosen. A section not listed falls through to the empty-`sections` entry, which is one attribute per column. Columns that hold a single value are still mapped honestly — an undiscriminating level simply draws no rung. */
  sectionSemantics: [
    {
      // Sheets are the only fabric care form that puts a sub-brand at cdt3 rather than a size; their scent sits at a CDT the ETL drops and is not in the loaded universe.
      sections: ["Fabric care"],
      when: { cdt2: ["Sheets"] },
      // The sub-brand also arrives in `flavour`, so sheets carry it twice; cdt3 stays the level and the duplicate is declared dead rather than mapped alongside it.
      drop: ["flavour"],
      columns: { cdt1: "brand", cdt2: "form", cdt3: "subBrand" },
    },
    {
      // Beads and Liquid put a clean size at cdt3 ("24 oz", "Large") and their sub-brand in `flavour`.
      sections: ["Fabric care"],
      when: { cdt2: ["Beads", "Liquid"] },
      columns: { cdt1: "brand", cdt2: "form", cdt3: "size", flavour: "subBrand" },
    },
    {
      // cdt3 is DELIBERATELY unmapped for the remaining forms: its meaning varies by form within this one section, and a section mapping cannot vary inside itself. Left unmapped, `size` stays the derived quantity, which is right for every form.
      sections: ["Fabric care"],
      columns: { cdt1: "brand", cdt2: "form", flavour: "subBrand" },
    },
    {
      sections: ["Detergent"],
      columns: { cdt1: "brand", cdt2: "form", cdt3: "packType" },
    },
    {
      sections: ["Bleach & antibacterial"],
      columns: { cdt1: "brand", cdt2: "packType", cdt3: "size" },
    },
    {
      sections: ["Powder"],
      columns: { cdt1: "packType", cdt2: "brand", cdt3: "form" },
    },
    {
      // cdt3 is the FUNCTION here, and the section states no size, so `size` stays the derived quantity. No profile attribute names `function` deliberately: the bundled CSV has no such column, and a live attribute the profile does not name keeps the overlay's own label.
      sections: ["Stain removers"],
      columns: { cdt1: "packType", cdt2: "brand", cdt3: "function" },
    },
    { sections: [], columns: { cdt1: "brand", cdt2: "form", cdt3: "packType" } },
    // Annotated because the entries differ in shape, and the inferred union would otherwise carry an optional `cdt3?: undefined` that no `Record<string, string>` accepts.
  ] as readonly StudioSectionSemantics[],
  missingValues: ["na", "n/a", "n-a", "none", "unknown", "unspecified"],
  semanticRoles: {
    blockIdentity: ["section", "brand", "form", "handle"],
    vertical: ["section", "brand", "form", "handle"],
    brand: "brand",
    tier: "handle",
    quantity: "size",
    terminalVariant: "variant",
  },
  firstValueRules: [],
  lastValueRules: [
    {
      attribute: "variant",
      values: ["free & clear", "free and clear", "free clear", "sensitive", "hypoallergenic"],
    },
  ],
  preferredValueRules: {
    first: "^(original|regular)$",
    last: "frees*&?s*clear|sensitive|fragrance[ -]?free|unscented",
  },
  quantityNormalization: {
    enabled: true,
    // The item-master quantity column is authoritative for this profile; name inference stays available to profiles whose master leaves quantity blank.
    inferFromName: false,
    zeroIsMissing: true,
    liquidOuncesAreFluid: true,
    unitPreference: ["fl oz", "l", "ml", "oz", "lb", "loads", "ct"],
    liquidNameTerms: ["liquid", "fl oz", "fluid ounce"],
    unitAliases: {
      "fl oz": "fl oz",
      "fluid ounce": "fl oz",
      "fluid ounces": "fl oz",
      oz: "oz",
      ounce: "oz",
      ounces: "oz",
      lb: "lb",
      lbs: "lb",
      pound: "lb",
      pounds: "lb",
      ml: "ml",
      l: "l",
      liter: "l",
      liters: "l",
      litre: "l",
      litres: "l",
      ct: "ct",
      count: "ct",
      loads: "loads",
      pacs: "ct",
      pods: "ct",
    },
    minimumFamilyEvidence: 2,
    reconciliationRange: { minimum: 0.8, maximum: 2 },
    // Semantic names, not column positions: reconciliation runs after the adapter resolves each section's columns.
    reconciliationBlockAttributes: ["variant", "subCategory", "brand", "form", "packType"],
  },
  aestheticsRules: {
    brandAttr: "brand",
    tierAttr: "handle",
    sizeAttr: "size",
    flavourAttr: "variant",
    verticalAttrs: ["section", "brand", "form", "handle"],
    // This category is built as stripes throughout; nothing here is banded onto shelves.
    horizontalAttrs: [],
    levelOrder: ["section", "brand", "form", "handle"],
    tierOrder: [
      { label: "light", values: ["Side handle", "Soft", "Soft pack", "Pouch", "Bag"] },
      { label: "heavy", values: ["Top handle", "Hard", "Hard box", "Tub"] },
    ],
    lastInBlock: ["free & clear", "free and clear", "free clear", "sensitive", "hypoallergenic"],
    bayOwners: ["Tiles", "Tile", "EVO"],
    bulkPounds: 8,
    heavyShare: 0.25,
    lowerShelfShare: 0.5,
    jitterPacks: 1,
    stackSeamMayShift: true,
    raggedZeroPacks: 4,
    flagsPerHundredZero: 20,
    levelWeightDecay: 0.6,
    contiguityBreakCost: 0.5,
    contiguityInterleaveFactor: 2,
    blockPieceCost: 1,
    blockFillFloor: 0.5,
    approvalThreshold: 90,
    unplacedTolerance: 0.02,
    approvalLevels: 2,
    staircaseFill: 0.7,
    approvalFullness: 0.95,
    severityPenalty: { blocking: 1, toCheck: 0.4, toReview: 0.15 },
    weights: { blocking: 57, cdt: 16, fullness: 16, rulesKept: 5, ergonomics: 1, range: 5 },
    blockingSplit: { shape: 0.425, edges: 0.425, ladder: 0.15 },
    cdtSplit: { detector: 0.6, ordering: 0.4 },
    shelfSenseSplit: { heavyLow: 0.5, bigUnderSmall: 0.5 },
    rangeSplit: { skusPlaced: 0.6, brandsKept: 0.4 },
  },
  hierarchyMoveRules: {
    minFacingsPerShelf: 2,
    roundTripFloorShare: 530 / 556,
    fillFloorShare: 0.989,
    fillDropTolerancePts: 0.5,
    seamPass: "on",
  },
  verticalBlocking: {
    blockShare: 0.55,
    fragmentJoinPct: DEFAULT_FRAGMENT_JOIN_PCT,
    minFillRatio: 0.9,
    preserveRange: true,
    onlyIfBetter: true,
  },
} satisfies StudioProfile;

export const WALMART_STUDIO_PROFILE: StudioProfile = walmartStudioProfile;

export const walmartItemMasterCompilation = compileStudioProfile(walmartStudioProfile);

/** Compatibility exports used by the currently embedded Studio panel. */
export const WALMART_ITEM_MASTER_PREFERRED_ORDER = walmartItemMasterCompilation.preferredOrder;
export const walmartItemMasterAdapterOptions = walmartItemMasterCompilation.adapterOptions;
